import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, ChevronDown, CircleHelp, CreditCard, LogOut, Settings, UserRound } from 'lucide-react';
import { useT } from '../../../lib/i18n';

/** The person's photo, or their initial on a coloured circle. */
export function Avatar({ src, name, size = 36 }) {
  return src
    ? <img className="sh-avatar" src={src} alt="" width={size} height={size} style={{ width: size, height: size }} />
    : <span className="sh-avatar initial" style={{ width: size, height: size, fontSize: size * 0.42 }}>{(name || '?').trim().charAt(0).toUpperCase()}</span>;
}

/** Top-right account button: name, role and a menu (profile, settings, billing, notifications, help, sign out). */
export default function ProfileMenu({ name, email, role, avatar, canBill, onSignOut }) {
  const { t } = useT();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const go = (to) => { setOpen(false); navigate(to); };
  const items = [
    [UserRound, t('View Profile'), t('Manage your personal details'), '/settings/profile'],
    [Settings, t('Account Settings'), t('Profile, preferences & security'), '/settings/appearance'],
    canBill && [CreditCard, t('Billing & Plan'), t('Manage subscription'), '/billing'],
    [Bell, t('Notifications'), t('Alerts & notification preferences'), '/settings/notifications'],
  ].filter(Boolean);

  return (
    <div className="sh-profile" ref={ref}>
      <button type="button" className="sh-profile-button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
        <Avatar src={avatar} name={name} size={38} />
        <span className="sh-profile-copy"><b>{name}</b><small>{role}</small></span>
        <ChevronDown size={16} className={open ? 'flip' : ''} />
      </button>
      {open && (
        <div className="sh-menu" role="menu" aria-label={t('Account')}>
          <div className="sh-menu-head">
            <Avatar src={avatar} name={name} size={48} />
            <div><b>{name}</b><small>{role}</small><small className="sh-email">{email}</small><span className="sh-online"><i />{t('Online')}</span></div>
          </div>
          {items.map(([Icon, title, sub, to]) => (
            <button type="button" role="menuitem" key={to} onClick={() => go(to)}>
              <Icon size={18} /><span><b>{title}</b><small>{sub}</small></span>
            </button>
          ))}
          <div className="sh-menu-sep" />
          <button type="button" role="menuitem" onClick={() => go('/support')}>
            <CircleHelp size={18} /><span><b>{t('Help & Support')}</b><small>{t('Get help or open a ticket')}</small></span>
          </button>
          <button type="button" role="menuitem" className="sh-signout" onClick={() => { setOpen(false); onSignOut(); }}>
            <LogOut size={18} /><span><b>{t('Sign Out')}</b></span>
          </button>
        </div>
      )}
    </div>
  );
}
